import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { CrisisDialog } from "@/components/Crisis";
import { ShareButton } from "@/components/InstallShare";
import {
  LayoutDashboard, MessageCircleHeart, NotebookPen, SmilePlus,
  Sparkles, Users, BookHeart, Settings, LogOut, Menu, X, LifeBuoy, Shield, Crown,
} from "lucide-react";

const nav = [
  { to: "/app", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/companion", label: "Companion", icon: MessageCircleHeart, testid: "nav-companion" },
  { to: "/app/journal", label: "Journal", icon: NotebookPen, testid: "nav-journal" },
  { to: "/app/mood", label: "Mood", icon: SmilePlus, testid: "nav-mood" },
  { to: "/app/tools", label: "Tools", icon: Sparkles, testid: "nav-tools" },
  { to: "/app/therapists", label: "Therapists", icon: Users, testid: "nav-therapists" },
  { to: "/app/resources", label: "Resources", icon: BookHeart, testid: "nav-resources" },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [openMobile, setOpenMobile] = useState(false);
  const [crisisOpen, setCrisisOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const openCrisis = () => {
    setOpenMobile(false);
    setCrisisOpen(true);
  };

  const SideContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 py-7">
        <NavLink to="/gateway" className="font-serif text-3xl text-brand tracking-tight" data-testid="brand-logo">
          Aurea
        </NavLink>
        <p className="text-xs text-ink-mute mt-1 tracking-wide">depth-led wellbeing</p>
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={() => setOpenMobile(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors duration-300 ${
                isActive ? "bg-brand text-linen" : "text-ink-2 hover:bg-linen-2"
              }`
            }
          >
            <n.icon className="w-[18px] h-[18px]" />
            {n.label}
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <NavLink to="/app/admin" data-testid="nav-admin" onClick={() => setOpenMobile(false)}
            className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors duration-300 ${isActive ? "bg-brand text-linen" : "text-ink-2 hover:bg-linen-2"}`}>
            <Shield className="w-[18px] h-[18px]" />
            Moderation
          </NavLink>
        )}
        <button
          onClick={openCrisis}
          data-testid="crisis-button"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm bg-accent/10 text-accent hover:bg-accent hover:text-linen transition-colors duration-300 mt-2"
        >
          <LifeBuoy className="w-[18px] h-[18px]" />
          Need help now?
        </button>
        <NavLink
          to="/app/plus"
          data-testid="nav-plus"
          onClick={() => setOpenMobile(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors duration-300 mt-1 ${
              isActive ? "bg-brand text-linen" : "text-brand bg-calm/50 hover:bg-calm"
            }`
          }
        >
          <Crown className="w-[18px] h-[18px]" />
          {user?.plan === "plus" ? "Aurea Plus" : "Upgrade to Plus"}
        </NavLink>
      </nav>
      <div className="p-4 mt-auto">
        <div className="bg-linen-2 rounded-2xl p-4">
          <p className="text-sm font-medium text-ink truncate">{user?.name}</p>
          <p className="text-xs text-ink-mute truncate">{user?.email}</p>
          {user?.plan === "plus" && (
            <span className="inline-flex items-center gap-1 mt-2 rounded-full bg-brand/10 text-brand px-2.5 py-0.5 text-[11px] font-medium" data-testid="plus-badge">
              <Crown className="w-3 h-3" /> Plus member
            </span>
          )}
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="mt-3 flex items-center gap-2 text-xs text-ink-2 hover:text-accent transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
          <ShareButton className="mt-2 flex items-center gap-2 text-xs text-ink-2 hover:text-brand transition-colors" testid="sidebar-share" />
        </div>
        <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-ink-mute">
          <NavLink to="/terms" onClick={() => setOpenMobile(false)} className="hover:text-brand transition-colors" data-testid="footer-terms">Terms</NavLink>
          <span>·</span>
          <NavLink to="/privacy" onClick={() => setOpenMobile(false)} className="hover:text-brand transition-colors" data-testid="footer-privacy">Privacy</NavLink>
        </div>
        <p className="text-[11px] text-ink-mute text-center mt-1" data-testid="copyright">© {new Date().getFullYear()} Aurea · Damian Alan Moses · All rights reserved</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-linen flex">
      <aside className="hidden lg:flex w-72 shrink-0 border-r border-linen-3 bg-linen flex-col fixed h-screen">
        <SideContent />
      </aside>

      {openMobile && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-ink/30" onClick={() => setOpenMobile(false)} />
          <aside className="relative w-72 bg-linen h-full border-r border-linen-3">
            <SideContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-72 min-w-0">
        <header className="lg:hidden flex items-center justify-between px-5 py-4 border-b border-linen-3 sticky top-0 bg-linen/90 backdrop-blur z-30">
          <span className="font-serif text-2xl text-brand">Aurea</span>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpenMobile(!openMobile)}>
            {openMobile ? <X /> : <Menu />}
          </button>
        </header>
        <main className="p-6 sm:p-8 lg:p-12 max-w-6xl mx-auto">
          <Outlet />
        </main>
      </div>
      <CrisisDialog open={crisisOpen} onOpenChange={setCrisisOpen} />
    </div>
  );
};

export default Layout;
